import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import {
  $isDiffHunkNode,
  DiffHunkNode,
} from '../customNodes/DiffHunkNode/DiffHunkNode';
import { $resolveDiffHunkInPlace } from '../customNodes/DiffHunkNode/DiffHunk';
import { useImpressionEditorContext } from '../../Context';
import { fireSuggestionTraceScore } from '../../../../../../../util/zoriEditingExtensions';

const messages = defineMessages({
  pendingCount: {
    id: 'markdownEditor.diffHunkBulkActions.pendingCount',
    defaultMessage:
      '{count, plural, one {# requested edit} other {# requested edits}} pending review',
  },
  acceptAll: {
    id: 'markdownEditor.diffHunkBulkActions.acceptAll',
    defaultMessage: 'Accept all',
  },
  rejectAll: {
    id: 'markdownEditor.diffHunkBulkActions.rejectAll',
    defaultMessage: 'Reject all',
  },
});

/**
 * Renders the pending-count label and "Reject all / Accept all" controls for
 * the active diff-review session. Designed to slot into the editor's bottom
 * save bar in place of the normal Discard/Save pair whenever a
 * `reviewDiffDocument` is present, so reviewers can resolve all suggestions
 * from the same spot they'd otherwise commit/cancel a manual edit.
 *
 * Must be rendered inside a `LexicalComposer` context. Each bulk action is
 * dispatched as a single `editor.update()` so `AllHunksResolvedPlugin` fires
 * once at the end and persists the resolved markdown.
 */
export const DiffHunkBulkActions: React.FC = () => {
  const { formatMessage } = useIntl();
  const [editor] = useLexicalComposerContext();
  const { sendAgentFeedback, activeImpression } = useImpressionEditorContext();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const readCount = () => {
      editor.getEditorState().read(() => {
        const count = $getRoot()
          .getChildren()
          .filter(node => $isDiffHunkNode(node)).length;
        setPendingCount(count);
      });
    };
    readCount();
    return editor.registerUpdateListener(() => readCount());
  }, [editor]);

  if (pendingCount === 0) {
    return null;
  }

  const resolveAll = (keep: 'added' | 'removed') => {
    const n = pendingCount;
    if (n > 0) {
      fireSuggestionTraceScore(
        sendAgentFeedback,
        activeImpression.suggestionTraceId,
        keep === 'added' ? 'accepted' : 'rejected',
        keep === 'added' ? `bulk accept (${n})` : `bulk reject (${n})`,
      );
    }
    editor.update(() => {
      const hunks = $getRoot()
        .getChildren()
        .filter((node): node is DiffHunkNode => $isDiffHunkNode(node));
      for (const hunk of hunks) {
        $resolveDiffHunkInPlace(hunk, keep);
      }
    });
  };

  return (
    <>
      <Box align="center" gap={1} data-testid="diff-hunk-bulk-actions">
        <Text size="subbody">
          {formatMessage(messages.pendingCount, { count: pendingCount })}
        </Text>
      </Box>
      <Box justify="center" gap={1}>
        <Button
          variant="text"
          color="negative"
          onClick={() => resolveAll('removed')}
          data-testid="diff-hunk-reject-all"
        >
          {formatMessage(messages.rejectAll)}
        </Button>
        <Button
          variant="contained"
          onClick={() => resolveAll('added')}
          data-testid="diff-hunk-accept-all"
        >
          {formatMessage(messages.acceptAll)}
        </Button>
      </Box>
    </>
  );
};
